import type { DailyHealthRecord, HealthImportSession } from "./dailyHealthTypes";
import type { UpsertTimelineEventInput } from "../train/athleteTimeline";

function confidenceFromData(record: DailyHealthRecord): "high" | "medium" | "low" | "context_only" {
  if (record.dataQuality.confidence === "high") return "high";
  if (record.dataQuality.confidence === "medium") return "medium";
  return "low";
}

export function buildDailyHealthTimelineEvents(record: DailyHealthRecord): UpsertTimelineEventInput[] {
  const events: UpsertTimelineEventInput[] = [];
  const date = record.localDate;
  const recoveryTimestamp = record.updatedAt;
  const sleepTimestamp = record.sleep.sleepEnd ?? record.updatedAt;

  if (record.bodyComposition.bodyWeightKg !== null) {
    events.push({
      eventDate: date,
      timestamp: record.metricProvenance.bodyWeightKg?.endDate ?? recoveryTimestamp,
      canonicalTimestampIso: record.metricProvenance.bodyWeightKg?.endDate ?? recoveryTimestamp,
      eventType: "body_weight",
      title: "Body weight recorded",
      summary: `${record.bodyComposition.bodyWeightKg.toFixed(1)} kg imported from Apple Health.`,
      source: "imported_source",
      sourceId: `daily_health_body_weight:${date}`,
      tags: ["daily_health", "body_composition"],
      metrics: { weight_kg: record.bodyComposition.bodyWeightKg },
      confidence: confidenceFromData(record),
      notes: `Provenance: ${record.metricProvenance.bodyWeightKg?.sourceName ?? "Unknown source"}`
    });
  }

  if (record.bodyComposition.bodyFatPercentage !== null) {
    events.push({
      eventDate: date,
      timestamp: record.updatedAt,
      canonicalTimestampIso: record.updatedAt,
      eventType: "body_fat",
      title: "Body-fat measurement recorded",
      summary: `${record.bodyComposition.bodyFatPercentage.toFixed(1)}% body fat imported from Apple Health.`,
      source: "imported_source",
      sourceId: `daily_health_body_fat:${date}`,
      tags: ["daily_health", "body_composition"],
      metrics: { body_fat_percentage: record.bodyComposition.bodyFatPercentage },
      confidence: confidenceFromData(record),
      notes: `Provenance: ${record.metricProvenance.bodyFatPercentage?.sourceName ?? "Unknown source"}`
    });
  }

  if (record.bodyComposition.waistCircumferenceCm !== null) {
    events.push({
      eventDate: date,
      timestamp: record.updatedAt,
      canonicalTimestampIso: record.updatedAt,
      eventType: "waist",
      title: "Waist measurement recorded",
      summary: `${record.bodyComposition.waistCircumferenceCm.toFixed(1)} cm waist imported from Apple Health.`,
      source: "imported_source",
      sourceId: `daily_health_waist:${date}`,
      tags: ["daily_health", "body_composition"],
      metrics: { waist_cm: record.bodyComposition.waistCircumferenceCm },
      confidence: confidenceFromData(record),
      notes: `Provenance: ${record.metricProvenance.waistCircumferenceCm?.sourceName ?? "Unknown source"}`
    });
  }

  if (
    record.recovery.hrvSdnnMs !== null
    || record.recovery.hrvRmssdMs !== null
    || record.recovery.restingHeartRateBpm !== null
    || record.recovery.respiratoryRateBpm !== null
    || record.recovery.wristTemperatureCelsius !== null
    || record.recovery.oxygenSaturationPercentage !== null
  ) {
    events.push({
      eventDate: date,
      timestamp: recoveryTimestamp,
      canonicalTimestampIso: recoveryTimestamp,
      eventType: "recovery",
      title: "Recovery metrics recorded",
      summary: `Recovery context imported${record.recovery.hrvSdnnMs !== null ? ` · HRV ${record.recovery.hrvSdnnMs.toFixed(0)} ms` : ""}${record.recovery.restingHeartRateBpm !== null ? ` · RHR ${record.recovery.restingHeartRateBpm.toFixed(0)} bpm` : ""}.`,
      source: "imported_source",
      sourceId: `daily_health_recovery:${date}`,
      tags: ["daily_health", "recovery"],
      metrics: {
        hrvSdnnMs: record.recovery.hrvSdnnMs ?? 0,
        hrvRmssdMs: record.recovery.hrvRmssdMs ?? 0,
        restingHeartRateBpm: record.recovery.restingHeartRateBpm ?? 0,
        respiratoryRateBpm: record.recovery.respiratoryRateBpm ?? 0,
        wristTemperatureCelsius: record.recovery.wristTemperatureCelsius ?? 0,
        oxygenSaturationPercentage: record.recovery.oxygenSaturationPercentage ?? 0
      },
      confidence: confidenceFromData(record),
      notes: `Provenance: ${record.sourceSummaries.map(source => source.name).join(", ") || "Apple Health"}`
    });
  }

  if (record.sleep.totalSleepMinutes !== null || record.sleep.sleepStart || record.sleep.sleepEnd) {
    events.push({
      eventDate: date,
      timestamp: sleepTimestamp,
      canonicalTimestampIso: sleepTimestamp,
      eventType: "sleep",
      title: "Sleep recorded",
      summary: `${record.sleep.totalSleepMinutes !== null ? `${Math.floor(record.sleep.totalSleepMinutes / 60)}h ${Math.round(record.sleep.totalSleepMinutes % 60)}m sleep` : "Sleep session"} imported from Apple Health.`,
      source: "imported_source",
      sourceId: `daily_health_sleep:${date}`,
      tags: ["daily_health", "sleep"],
      metrics: {
        totalSleepMinutes: record.sleep.totalSleepMinutes ?? 0,
        sleepEfficiencyPercentage: record.sleep.sleepEfficiencyPercentage ?? 0,
        deepSleepMinutes: record.sleep.deepSleepMinutes ?? 0,
        remSleepMinutes: record.sleep.remSleepMinutes ?? 0
      },
      confidence: confidenceFromData(record),
      notes: `Provenance: ${record.sleep.sourceNames.join(", ") || "Apple Health"}`
    });
  }

  if (record.cardiovascular.systolicBloodPressure !== null || record.cardiovascular.diastolicBloodPressure !== null) {
    events.push({
      eventDate: date,
      timestamp: record.updatedAt,
      canonicalTimestampIso: record.updatedAt,
      eventType: "bloodwork",
      title: "Blood pressure measurement recorded",
      summary: `${record.cardiovascular.systolicBloodPressure ?? "--"}/${record.cardiovascular.diastolicBloodPressure ?? "--"} mmHg imported from Apple Health.`,
      source: "imported_source",
      sourceId: `daily_health_bp:${date}`,
      tags: ["daily_health", "cardiovascular"],
      metrics: {
        systolic: record.cardiovascular.systolicBloodPressure ?? 0,
        diastolic: record.cardiovascular.diastolicBloodPressure ?? 0
      },
      confidence: confidenceFromData(record),
      notes: `Provenance: ${record.metricProvenance.systolicBloodPressure?.sourceName ?? record.metricProvenance.diastolicBloodPressure?.sourceName ?? "Unknown source"}`
    });
  }

  if (record.cardiovascular.vo2MaxMlKgMin !== null) {
    events.push({
      eventDate: date,
      timestamp: record.updatedAt,
      canonicalTimestampIso: record.updatedAt,
      eventType: "recovery",
      title: "VO2 max measurement updated",
      summary: `VO2 max updated to ${record.cardiovascular.vo2MaxMlKgMin.toFixed(1)} ml/kg/min from Apple Health import.`,
      source: "imported_source",
      sourceId: `daily_health_vo2:${date}`,
      tags: ["daily_health", "cardiovascular"],
      metrics: { vo2max: record.cardiovascular.vo2MaxMlKgMin },
      confidence: confidenceFromData(record),
      notes: `Provenance: ${record.metricProvenance.vo2MaxMlKgMin?.sourceName ?? "Unknown source"}`
    });
  }

  return events;
}

export function buildImportCompletedTimelineEvent(session: HealthImportSession): UpsertTimelineEventInput {
  return {
    eventDate: session.dateRangeEnd ?? session.startedAt.slice(0, 10),
    timestamp: session.completedAt ?? session.startedAt,
    canonicalTimestampIso: session.completedAt ?? session.startedAt,
    eventType: "health_import",
    title: "Apple Health import completed",
    summary: `${session.recordsImported} daily records imported from ${session.fileName}.`,
    source: "imported_source",
    sourceId: `daily_health_import:${session.importFingerprint}`,
    tags: ["daily_health", "import"],
    metrics: {
      recordsImported: session.recordsImported,
      recordsSupported: session.recordsSupported,
      recordsIgnored: session.recordsIgnored
    },
    confidence: "context_only",
    notes: `Provenance: Apple Health export import session ${session.id}`
  };
}
